import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, CheckCircle, AlertCircle, ArrowRight, Clock, 
  DollarSign, Building2, Smartphone, Monitor, HelpCircle,
  ChevronDown, ChevronUp, Zap, Shield, Star, Users
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const NotaFiscalMeiPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const navigateTo = (url) => {
    window.location.href = url;
  };

  // Passos para emitir nota fiscal
  const steps = [
    {
      number: '01',
      title: 'Cadastre-se no MeuMei',
      description: 'Crie sua conta gratuita em menos de 2 minutos. Se já tem MEI, basta vincular seu CNPJ.',
      icon: Users,
      color: 'from-blue-500 to-blue-600'
    },
    {
      number: '02',
      title: 'Configure sua empresa',
      description: 'Adicione os dados da sua empresa: razão social, endereço e atividade principal (CNAE).',
      icon: Building2,
      color: 'from-purple-500 to-purple-600'
    },
    {
      number: '03',
      title: 'Preencha os dados da nota',
      description: 'Informe o cliente, serviço/produto prestado, valor e descrição. Super simples!',
      icon: FileText,
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      number: '04',
      title: 'Emita e envie',
      description: 'Clique em emitir e pronto! A nota é gerada e enviada automaticamente por e-mail.',
      icon: Zap,
      color: 'from-amber-500 to-amber-600'
    }
  ];

  // Tipos de nota fiscal
  const tiposNota = [
    {
      tipo: 'NFS-e',
      nome: 'Nota Fiscal de Serviços Eletrônica',
      descricao: 'Para quem presta serviços: cabeleireiros, eletricistas, consultores, designers, etc.',
      exemplos: ['Corte de cabelo', 'Manutenção elétrica', 'Desenvolvimento de site', 'Consultoria'],
      cor: 'emerald'
    },
    {
      tipo: 'NF-e',
      nome: 'Nota Fiscal Eletrônica (Produtos)',
      descricao: 'Para quem vende produtos físicos: artesanato, comidas, roupas, etc.',
      exemplos: ['Venda de bolos', 'Artesanato', 'Roupas', 'Acessórios'],
      cor: 'blue'
    },
    {
      tipo: 'NFC-e',
      nome: 'Nota Fiscal ao Consumidor',
      descricao: 'Para vendas diretas ao consumidor final em comércio varejista.',
      exemplos: ['Loja física', 'Feira', 'Quiosque', 'Food truck'],
      cor: 'purple'
    }
  ];

  // Benefícios
  const beneficios = [
    {
      icon: Shield,
      title: 'Credibilidade',
      description: 'Transmite profissionalismo e confiança para seus clientes'
    },
    {
      icon: Building2,
      title: 'Vendas para Empresas',
      description: 'Permite vender para outras empresas que exigem nota fiscal'
    },
    {
      icon: DollarSign,
      title: 'Controle Financeiro',
      description: 'Acompanhe seu faturamento e mantenha suas finanças organizadas'
    },
    {
      icon: FileText,
      title: 'Comprovação de Renda',
      description: 'Use as notas como comprovante de renda para financiamentos'
    }
  ];

  // FAQ
  const faqs = [
    {
      pergunta: 'MEI é obrigado a emitir nota fiscal?',
      resposta: 'O MEI só é obrigado a emitir nota fiscal quando vende para outras empresas (pessoa jurídica). Para vendas para pessoas físicas (consumidor final), a emissão é opcional, mas recomendada para controle e credibilidade.'
    },
    {
      pergunta: 'Quanto custa emitir nota fiscal sendo MEI?',
      resposta: 'A emissão de nota fiscal em si é gratuita. Você só precisa de uma plataforma para gerenciar suas notas. Com o MeuMei, você emite notas ilimitadas no plano PRO por apenas R$ 29,90/mês.'
    },
    {
      pergunta: 'Preciso de certificado digital para emitir nota fiscal?',
      resposta: 'Na maioria das cidades, o MEI está dispensado do certificado digital para emissão de NFS-e. Porém, algumas prefeituras podem exigir. Com o MeuMei, verificamos automaticamente a necessidade da sua cidade.'
    },
    {
      pergunta: 'Como emitir nota fiscal pelo celular?',
      resposta: 'Com o MeuMei você emite notas fiscais direto pelo celular! Nossa plataforma é 100% responsiva e funciona perfeitamente em qualquer dispositivo. Baixe o app ou acesse pelo navegador.'
    },
    {
      pergunta: 'O que acontece se eu não emitir nota fiscal?',
      resposta: 'Se você vender para empresas sem emitir nota, pode ser multado pela Receita Federal. Além disso, perde credibilidade e não consegue comprovar seu faturamento. Sempre emita nota, mesmo quando não obrigatório!'
    },
    {
      pergunta: 'Posso cancelar uma nota fiscal emitida?',
      resposta: 'Sim! É possível cancelar uma nota fiscal dentro do prazo estabelecido pela prefeitura (geralmente 24 a 48 horas). Após esse prazo, é necessário emitir uma nota de estorno ou carta de correção.'
    },
    {
      pergunta: 'MEI pode emitir nota fiscal para outro estado?',
      resposta: 'Sim! O MEI pode emitir nota fiscal para clientes de qualquer estado do Brasil. Para serviços, você emite NFS-e normalmente. Para produtos, pode haver incidência de ICMS interestadual.'
    },
    {
      pergunta: 'Como calcular o valor da nota fiscal MEI?',
      resposta: 'O valor da nota fiscal deve ser o valor total do serviço ou produto vendido. O MEI não destaca impostos na nota pois já paga tudo através do DAS mensal (valor fixo). Basta colocar o valor combinado com o cliente.'
    }
  ];

  return (
    <div className="min-h-screen font-sans bg-white">
      {/* Schema.org para SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Como Emitir Nota Fiscal MEI: Guia Completo 2025",
          "description": "Aprenda como emitir nota fiscal sendo MEI. Passo a passo completo, tipos de nota, obrigatoriedade e dicas práticas.",
          "author": { "@type": "Organization", "name": "MeuMei" },
          "publisher": { "@type": "Organization", "name": "MeuMei", "logo": { "@type": "ImageObject", "url": "https://meumei.net/logo.png" } },
          "datePublished": "2025-01-01",
          "dateModified": "2025-01-01"
        })
      }} />

      <Header />

      {/* Hero Section */}
      <section 
        className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
        }}
      >
        {/* Decorações de fundo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
          <div className="absolute top-1/2 left-10 w-20 h-20 rounded-full bg-white/10" />
          <div className="absolute bottom-20 right-20 w-32 h-32 rounded-full bg-white/5" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          {/* Breadcrumb */}
          <nav className="text-white/70 text-sm mb-6">
            <a href="/" className="hover:text-white">Início</a>
            <span className="mx-2">›</span>
            <a href="/blog" className="hover:text-white">Blog</a>
            <span className="mx-2">›</span>
            <span className="text-white">Nota Fiscal MEI</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            {/* Badge */}
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <FileText className="w-4 h-4" />
              Guia Completo 2025
            </motion.span>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Como Emitir <span className="text-yellow-300">Nota Fiscal MEI</span>
            </h1>
            
            <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed opacity-90 mb-8">
              Aprenda tudo sobre emissão de nota fiscal para Microempreendedor Individual. 
              Passo a passo, tipos de nota, obrigatoriedade e muito mais!
            </p>

            {/* Meta info */}
            <div className="flex flex-wrap justify-center gap-4 text-sm opacity-80">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                8 min de leitura
              </span>
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                Atualizado em Jan/2025
              </span>
            </div>
          </motion.div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* CTA Flutuante Mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg z-40 md:hidden">
        <motion.button
          onClick={() => navigateTo('https://app.meumei.net/register')}
          whileTap={{ scale: 0.98 }}
          className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl flex items-center justify-center gap-2"
        >
          <Zap className="w-5 h-5" />
          Emitir Nota Fiscal Grátis
        </motion.button>
      </div>

      {/* Conteúdo Principal */}
      <article className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Introdução */}
          <section className="prose prose-lg max-w-none mb-16">
            <p className="text-xl text-gray-600 leading-relaxed">
              Emitir <strong>nota fiscal MEI</strong> é mais simples do que você imagina! Neste guia completo, 
              vamos te mostrar <strong>passo a passo</strong> como emitir nota fiscal sendo Microempreendedor Individual, 
              quais os tipos de nota, quando é obrigatório e como fazer tudo pelo celular.
            </p>

            {/* Box de destaque */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 p-6 rounded-r-xl my-8"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-emerald-800 text-lg mb-2">Quer emitir nota fiscal agora?</h3>
                  <p className="text-emerald-700 mb-3">
                    Com o MeuMei você emite sua primeira nota fiscal em menos de 5 minutos. 
                    Cadastre-se grátis e teste por 7 dias!
                  </p>
                  <a 
                    href="https://app.meumei.net/register"
                    className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700"
                  >
                    Começar agora <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </motion.div>
          </section>

          {/* O que é Nota Fiscal */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              O que é Nota Fiscal e por que o MEI precisa emitir?
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              A <strong>nota fiscal</strong> é um documento que comprova a venda de um produto ou prestação de um serviço. 
              Para o MEI, emitir nota fiscal traz diversas vantagens:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {beneficios.map((beneficio, index) => (
                <motion.div
                  key={beneficio.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                    <beneficio.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{beneficio.title}</h3>
                  <p className="text-gray-600">{beneficio.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* MEI é obrigado? */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              MEI é obrigado a emitir nota fiscal?
            </h2>
            
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-amber-800 mb-2">Atenção!</h3>
                  <p className="text-amber-700">
                    O MEI <strong>é obrigado</strong> a emitir nota fiscal quando vende para <strong>outras empresas</strong> (pessoa jurídica). 
                    Para vendas ao <strong>consumidor final</strong> (pessoa física), a emissão é <strong>opcional</strong>, mas altamente recomendada.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <h4 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Obrigatório emitir
                </h4>
                <ul className="space-y-2 text-red-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    Vendas para empresas (CNPJ)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    Vendas para órgãos públicos
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    Quando o cliente solicitar
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Opcional (mas recomendado)
                </h4>
                <ul className="space-y-2 text-green-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    Vendas para pessoas físicas
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    Vendas em dinheiro/Pix
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    Serviços informais
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Tipos de Nota Fiscal */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              Tipos de Nota Fiscal para MEI
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Existem diferentes tipos de nota fiscal, e o tipo que você vai emitir depende da sua atividade:
            </p>

            <div className="space-y-6">
              {tiposNota.map((nota, index) => (
                <motion.div
                  key={nota.tipo}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-${nota.cor}-50 border border-${nota.cor}-200 rounded-2xl p-6`}
                  style={{
                    backgroundColor: nota.cor === 'emerald' ? '#ecfdf5' : nota.cor === 'blue' ? '#eff6ff' : '#faf5ff',
                    borderColor: nota.cor === 'emerald' ? '#a7f3d0' : nota.cor === 'blue' ? '#bfdbfe' : '#e9d5ff'
                  }}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <span 
                      className="text-2xl font-bold px-4 py-2 rounded-xl"
                      style={{
                        backgroundColor: nota.cor === 'emerald' ? '#10b981' : nota.cor === 'blue' ? '#3b82f6' : '#8b5cf6',
                        color: 'white'
                      }}
                    >
                      {nota.tipo}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{nota.nome}</h3>
                      <p className="text-gray-600 mb-4">{nota.descricao}</p>
                      <div className="flex flex-wrap gap-2">
                        {nota.exemplos.map((exemplo) => (
                          <span 
                            key={exemplo}
                            className="px-3 py-1 bg-white rounded-full text-sm text-gray-600 border"
                          >
                            {exemplo}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Passo a Passo */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              Como Emitir Nota Fiscal MEI: Passo a Passo
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Emitir nota fiscal com o MeuMei é super simples. Veja o passo a passo:
            </p>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-6 items-start"
                >
                  <div 
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0 shadow-lg`}
                  >
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 pt-2">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-bold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">
                        Passo {step.number}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-800 text-xl mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-10 text-center"
            >
              <motion.button
                onClick={() => navigateTo('https://app.meumei.net/register')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all"
              >
                <Zap className="w-5 h-5" />
                Começar a Emitir Notas Grátis
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              <p className="text-gray-500 text-sm mt-3">7 dias grátis • Sem cartão de crédito</p>
            </motion.div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              Perguntas Frequentes sobre Nota Fiscal MEI
            </h2>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="border border-gray-200 rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-800 pr-4">{faq.pergunta}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                          {faq.resposta}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden"
            >
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
              </div>

              <div className="relative z-10">
                <h2 className="text-2xl md:text-4xl font-bold mb-4">
                  Pronto para emitir sua primeira nota fiscal?
                </h2>
                <p className="text-lg md:text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                  Com o MeuMei você emite notas fiscais ilimitadas, controla suas finanças e 
                  mantém seu MEI sempre em dia. Comece agora!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.button
                    onClick={() => navigateTo('https://app.meumei.net/register')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-white text-emerald-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    Criar Conta Grátis
                  </motion.button>
                  <motion.button
                    onClick={() => navigateTo('/planos')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-white/20 text-white font-bold rounded-xl border-2 border-white/30 hover:bg-white/30 transition-all"
                  >
                    Ver Planos
                  </motion.button>
                </div>
                <p className="text-sm opacity-75 mt-4">
                  ✓ 7 dias grátis &nbsp; ✓ Sem cartão &nbsp; ✓ Cancele quando quiser
                </p>
              </div>
            </motion.div>
          </section>

        </div>
      </article>

      {/* Espaço para CTA fixo mobile */}
      <div className="h-24 md:hidden" />

      <Footer />
    </div>
  );
};

export default NotaFiscalMeiPage;